import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles } from "lucide-react";
import { submitFeedback } from "../lib/api";
import { track } from "../lib/analytics";

/**
 * MiniFeedbackToast
 * ─────────────────
 * One-line drop-off intercept. Shown when a user closes the AR mirror
 * early, abandons onboarding past the first step, or bounces off the
 * paywall. Catches the "almost converted" insights the analytics
 * dashboard alone can't surface.
 *
 * Design constraints:
 *  • Never blocks the UI — toast-style card, dismissible, auto-times-out.
 *  • One question, one submit, max 5 quick-pick chips → 0 friction.
 *  • All submissions go to /api/feedback so they land in /admin/feedback,
 *    tagged with the context (e.g. "[paywall_bounced]") so they're easy
 *    to slice.
 *  • Stealth-aware via `track()` (no events leak in stealth mode).
 *  • Context-specific copy: the question, sub-line, and chips all change
 *    based on what the user was doing — generic prompts get ignored.
 */

const REASON_SETS = {
  mirror_closed_early: [
    { value: "lighting", label: "Lighting was tricky" },
    { value: "camera", label: "Camera trouble" },
    { value: "guidance", label: "Couldn't see what to do" },
    { value: "exploring", label: "Just exploring" },
    { value: "other", label: "Something else…" },
  ],
  onboarding_abandoned: [
    { value: "long", label: "Felt long" },
    { value: "unclear", label: "Wasn't sure what to pick" },
    { value: "tech", label: "Something broke" },
    { value: "exploring", label: "Just exploring" },
    { value: "other", label: "Something else…" },
  ],
  paywall_bounced: [
    { value: "price", label: "Price was too high" },
    { value: "value", label: "Wasn't sure it's worth it" },
    { value: "now", label: "Not the right moment" },
    { value: "free_enough", label: "Free version is enough" },
    { value: "other", label: "Something else…" },
  ],
};

const PROMPTS = {
  mirror_closed_early: {
    title: "Quick — what got in the way?",
    subtitle: "One tap helps me make this better.",
  },
  onboarding_abandoned: {
    title: "Quick — what made you pause?",
    subtitle: "One tap and I'll make the next visit smoother.",
  },
  paywall_bounced: {
    title: "Quick — what was missing?",
    subtitle: "Honest answers shape the next pricing tier.",
  },
};

const AUTO_DISMISS_MS = 25_000;

const MiniFeedbackToast = ({ context = "learn_with_trouve_closed_early", onClose }) => {
  const reasons = REASON_SETS[context] || REASON_SETS.mirror_closed_early;
  const prompt = PROMPTS[context] || PROMPTS.mirror_closed_early;
  const [reason, setReason] = useState(null);
  const [other, setOther] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const dismissTimer = useRef(null);

  useEffect(() => {
    track("mini_feedback_shown", { context });
    dismissTimer.current = setTimeout(() => {
      track("mini_feedback_auto_dismissed", { context });
      onClose?.();
    }, AUTO_DISMISS_MS);
    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    track("mini_feedback_dismissed", { context });
    onClose?.();
  };

  const submit = async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    try {
      const reasonLabel = reasons.find((r) => r.value === reason)?.label || reason;
      const message =
        reason === "other" && other.trim()
          ? `[${context}] Other — ${other.trim().slice(0, 800)}`
          : `[${context}] ${reasonLabel}`;
      await submitFeedback({
        message,
        page_url: typeof window !== "undefined" ? window.location.pathname : null,
      });
      track("mini_feedback_submitted", { context, reason });
      setDone(true);
      // Show "Thank you" briefly, then close
      setTimeout(() => onClose?.(), 1400);
    } catch {
      // If the network call fails, just close — analytics already logged.
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 60, opacity: 0 }}
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100vw-32px)] max-w-md"
      data-testid="mini-feedback-toast"
    >
      <div className="bg-white border border-[#EADDD7] rounded-3xl shadow-[0_18px_40px_rgba(45,30,40,0.18)] overflow-hidden">
        {!done ? (
          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="h-8 w-8 rounded-full bg-[#F2E8E9] flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-[#9B6A6C]" />
                </span>
                <div>
                  <div className="text-[15px] font-medium text-[#2C2123] leading-tight">
                    {prompt.title}
                  </div>
                  <div className="text-xs text-[#8A7D82] mt-0.5">
                    {prompt.subtitle}
                  </div>
                </div>
              </div>
              <button
                onClick={dismiss}
                className="h-8 w-8 rounded-full hover:bg-[#F5EFEB] flex items-center justify-center text-[#8A7D82] transition-colors shrink-0"
                aria-label="Dismiss"
                data-testid="mini-feedback-dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="mini-feedback-chips">
              {reasons.map((r) => {
                const selected = reason === r.value;
                return (
                  <button
                    key={r.value}
                    onClick={() => setReason(r.value)}
                    className={`text-xs rounded-full px-3 py-1.5 transition-all ${
                      selected
                        ? "bg-[#9B6A6C] text-white"
                        : "border border-[#EADDD7] text-[#5C4B51] hover:border-[#D5A8A4] hover:text-[#5D4050]"
                    }`}
                    data-testid={`mini-feedback-chip-${r.value}`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>

            <AnimatePresence>
              {reason === "other" && (
                <motion.input
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  type="text"
                  maxLength={800}
                  value={other}
                  onChange={(e) => setOther(e.target.value)}
                  placeholder="A few words help most…"
                  className="w-full rounded-xl border border-[#EADDD7] bg-[#FDFBF7] px-3 py-2 text-sm text-[#2C2123] placeholder:text-[#B8A8AB] focus:outline-none focus:border-[#9B6A6C] focus:ring-2 focus:ring-[#F2E8E9] transition-all mb-3"
                  data-testid="mini-feedback-other-input"
                  autoFocus
                />
              )}
            </AnimatePresence>

            <button
              onClick={submit}
              disabled={!reason || submitting || (reason === "other" && !other.trim())}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-40 disabled:cursor-not-allowed rounded-full px-5 py-2.5 text-sm transition-all"
              data-testid="mini-feedback-submit"
            >
              <Send className="h-3.5 w-3.5" />
              {submitting ? "Sending…" : "Send"}
            </button>
          </div>
        ) : (
          <div className="p-6 text-center" data-testid="mini-feedback-thanks">
            <Sparkles className="h-5 w-5 text-[#9B6A6C] mx-auto mb-2" />
            <div className="text-[#2C2123] font-medium">Thank you — every word shapes this.</div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MiniFeedbackToast;
